import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/hooks/auth-provider'
import { useAuth } from '@/hooks/use-auth'
import { IamAppShell } from '@/components/layout/app-shell'
import { LoginPageWrapper as LoginPage } from '@/pages/login'
import { DashboardPage } from '@/pages/dashboard'
import { TenantsPage } from '@/pages/tenants'
import { BookmakersPage } from '@/pages/bookmakers'
import { UsersPage } from '@/pages/users'
import { RolesPage } from '@/pages/roles'
import { RoleDetailPage } from '@/pages/role-detail'
import { PermissionsPage } from '@/pages/permissions'
import { AssignmentsPage } from '@/pages/assignments'
import { AssignmentDetailPage } from '@/pages/assignment-detail'
import { AuditPage } from '@/pages/audit'
import { ProductsPage } from '@/pages/products'
import { AuditDetailPage } from '@/pages/audit-detail'
import { UserDetailPage } from '@/pages/user-detail'
import { TenantDetailPage } from '@/pages/tenant-detail'
import { ProfilePageWrapper as ProfilePage } from '@/pages/profile'
import type { ReactNode } from 'react'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
})

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth()
  if (isLoading) return <div className="flex items-center justify-center min-h-screen text-muted-foreground">Loading...</div>
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

function PublicRoute({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth()
  if (isLoading) return <div className="flex items-center justify-center min-h-screen text-muted-foreground">Loading...</div>
  if (user) return <Navigate to="/" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route element={<ProtectedRoute><IamAppShell /></ProtectedRoute>}>
              <Route index element={<DashboardPage />} />
              <Route path="tenants" element={<TenantsPage />} />
              <Route path="tenants/:id" element={<TenantDetailPage />} />
              <Route path="bookmakers" element={<BookmakersPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="users/:id" element={<UserDetailPage />} />
              <Route path="roles" element={<RolesPage />} />
              <Route path="roles/:id" element={<RoleDetailPage />} />
              <Route path="permissions" element={<PermissionsPage />} />
              <Route path="assignments" element={<AssignmentsPage />} />
              <Route path="assignments/:id" element={<AssignmentDetailPage />} />
              <Route path="audit" element={<AuditPage />} />
              <Route path="audit/:id" element={<AuditDetailPage />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
